import cn from "classnames"

import { DatePicker, Input, InputNumber, Select, TextArea } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import {
  OnDemandDateField,
  SprintRadioGroup
} from "@/molecules/JiraIssueField/fields"
import { JiraFormItem } from "@/molecules/JiraIssueField/JiraFormItem"
import { DynamicTagsFormItem } from "../DynamicTagsField"
import { getTicketFieldControl } from "./TicketField.helpers"
import { TicketOptionsSelect } from "./TicketOptionsSelect"

import styles from "./TicketField.module.scss"

import type { TicketFieldProps } from "./TicketField.types"

const ON_DEMAND_PREFIX = "onDemandFields"

export const TicketField = (props: TicketFieldProps) => {
  const { system, field, projectId, mode, form, disabled } = props
  const className = cn(styles.itemForm, props.className)
  const { previewMode, tags, initialValue, rules } = props
  const { t } = useTranslation("translation", { keyPrefix: "ticketModal" })
  const control = getTicketFieldControl(field, mode)
  const onDemandProps = {
    disabled,
    className,
    t,
    fieldKey: field.key,
    fieldName: field.name,
    label: field.name,
    required: field.required,
    formItemPrefix: ON_DEMAND_PREFIX
  }

  if (control === "onDemandDate")
    return <OnDemandDateField {...onDemandProps} />
  if (control === "onDemandSprint")
    return <SprintRadioGroup {...onDemandProps} />
  if (control === "templateInput")
    return (
      <DynamicTagsFormItem
        type="input"
        disabled={disabled}
        previewMode={previewMode}
        form={form}
        label={field.name}
        name={field.key}
        rules={rules}
        tags={tags}
        initialValue={initialValue}
      />
    )

  const options = field.allowedValues || []
  const controls = {
    remoteSelect: (
      <TicketOptionsSelect
        system={system}
        projectId={projectId}
        fieldKey={field.key}
        disabled={disabled}
      />
    ),
    remoteMultiSelect: (
      <TicketOptionsSelect
        multiple
        system={system}
        projectId={projectId}
        fieldKey={field.key}
        disabled={disabled}
      />
    ),
    select: <Select allowClear disabled={disabled} options={options} />,
    multiSelect: (
      <Select
        allowClear
        mode="multiple"
        disabled={disabled}
        options={options}
      />
    ),
    input: <Input allowClear disabled={disabled} />,
    textArea: <TextArea allowClear disabled={disabled} />,
    number: <InputNumber disabled={disabled} className={styles.inputNumber} />,
    date: <DatePicker disabled={disabled} />
  }

  return (
    <JiraFormItem
      fieldKey={field.key}
      className={className}
      label={field.name}
      required={field.required}
      formItemName={field.key}
      rules={rules}
    >
      {controls[control]}
    </JiraFormItem>
  )
}
